import React, { useState, useEffect } from 'react';
import { fmtDate as _fmtDate, humanizeDates } from '@/lib/dates';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight, ChevronLeft, X, Inbox } from 'lucide-react';

const CLOSED = ['Cancelled', 'Closed by Admin', 'Closed', 'Completed', 'Work Completed'];
const COMPLETED = ['Completed', 'Work Completed', 'Closed by Admin', 'Closed'];
const reqDate = (w) => w.required_completion_date || w.required_service_date;
const isActive = (w) => !CLOSED.includes(w.status);
const isOverdue = (w) => {
  const d = reqDate(w);
  if (!d || !isActive(w)) return false;
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return new Date(d) < t;
};
const matchesSpecial = (w, s) => {
  if (s === 'active') return isActive(w);
  if (s === 'completed') return COMPLETED.includes(w.status);
  if (s === 'overdue') return isOverdue(w);
  return true;
};
const SPECIAL_LABEL = { active: 'Open', completed: 'Completed', overdue: 'Overdue' };
const fmtShort = (s) => (_fmtDate(s) || s);

// Read-only, customer-scoped Work Order list for a Facilities Director drilling in from Reports.
// Data comes from the API-scoped /work-orders endpoint (already sanitized), so no cross-customer
// or internal fields are ever present here.
const ClientWorkOrderList = ({ allWos, filter, setFilter, onOpen, onBack }) => {
  const [f, setF] = useState(filter || {});
  useEffect(() => { setF(filter || {}); }, [filter]);

  const clear = (key) => {
    const n = { ...f };
    if (key === 'range') { delete n.start; delete n.end; }
    else { delete n[key]; if (key === 'location_id') delete n.location_name; }
    setF(n); setFilter?.(n);
  };
  const clearAll = () => { setF({}); setFilter?.({}); };

  const chips = [
    f.special && { key: 'special', label: SPECIAL_LABEL[f.special] || f.special },
    (f.start || f.end) && f.special !== 'overdue' && { key: 'range', label: `${f.start ? fmtShort(f.start) : '…'} → ${f.end ? fmtShort(f.end) : '…'}` },
    f.location_id && { key: 'location_id', label: `Location: ${f.location_name || 'Selected'}` },
    f.trade_type && { key: 'trade_type', label: `Trade: ${f.trade_type}` },
    f.status && { key: 'status', label: `Status: ${f.status}` },
    f.priority && { key: 'priority', label: `Priority: ${f.priority}` },
  ].filter(Boolean);

  const inRange = (w) => {
    // Overdue is a current-state metric on the backend (not date-ranged) — match that here.
    if (f.special === 'overdue') return true;
    const dr = (w.date_received || '').slice(0, 10);
    if (f.start && (!dr || dr < f.start)) return false;
    if (f.end && (!dr || dr > f.end)) return false;
    return true;
  };

  const rows = (allWos || []).filter((w) => {
    if (f.special && !matchesSpecial(w, f.special)) return false;
    if (!inRange(w)) return false;
    if (f.location_id && w.location_id !== f.location_id) return false;
    if (f.trade_type && w.trade_type !== f.trade_type) return false;
    if (f.status && w.status !== f.status) return false;
    if (f.priority && w.priority_level !== f.priority) return false;
    return true;
  });

  return (
    <div data-testid="client-wo-list">
      <button onClick={onBack} data-testid="client-wo-list-back"
        className="mb-4 inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-[#0E2A47]">
        <ChevronLeft size={16} /> Back to Reports
      </button>
      <div className="mb-4">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#8FC93A]">Facilities Director</div>
        <h1 className="text-3xl font-extrabold text-[#0E2A47]">Work Orders</h1>
      </div>

      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="client-wo-chips">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Filtered by:</span>
          {chips.map((c) => (
            <span key={c.key} data-testid={`client-wo-chip-${c.key}`}
              className="inline-flex items-center gap-1.5 rounded-full bg-[#EAF0F8] border border-[#cfe0f2] px-3 py-1 text-xs font-medium text-[#0E2A47]">
              {c.label}
              <button onClick={() => clear(c.key)} data-testid={`client-wo-chip-clear-${c.key}`} className="rounded-full hover:bg-[#0E2A47]/10 p-0.5" aria-label="Clear filter">
                <X size={12} />
              </button>
            </span>
          ))}
          <button onClick={clearAll} data-testid="client-wo-chip-clear-all" className="text-xs font-medium text-slate-500 hover:text-[#0E2A47] underline">Clear all</button>
        </div>
      )}

      <Card>
        <CardContent className="p-0 divide-y divide-gray-100">
          {rows.map((w) => (
            <button key={w.id} onClick={() => onOpen(w.id)} data-testid={`client-wo-list-row-${w.id}`}
              className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-gray-50">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-800 truncate">{humanizeDates(w.job_name)}</div>
                <div className="text-xs text-slate-400 truncate">
                  <span className="font-mono">{w.tfs_work_order_number}</span> · {w.service_location}
                  {w.priority_level ? ` · ${w.priority_level}` : ''} · {w.status}
                </div>
              </div>
              {isOverdue(w) && <span className="hidden sm:inline-flex rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">Overdue</span>}
              <ChevronRight className="text-slate-300 shrink-0" />
            </button>
          ))}
          {rows.length === 0 && (
            <div className="py-16 text-center text-slate-400 text-sm">
              <Inbox size={30} className="mx-auto mb-2 text-slate-300" /> No work orders match this view.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientWorkOrderList;
